(ns manage.feed-importer-ocp.db
  (:require [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.form.core :as form]
            [common.loader.db :as loader.db]
            [common.ocp.db]
            [common.util :as util]
            [common.validators.core :refer [s3-url? url? valid-placeholder-name?]]
            [manage.resource.feed-imports :as feed-imports]
            [mate.re-frame :as mr]
            [phrase.alpha :as p]
            [re-frame.core :as rf]))

(def entity-name "Feed")

(defn distinct-elements?
  [k vs]
  (if (seq vs)
    (apply distinct? (mapv k vs))
    true))

(defn sku-used?
  [vs]
  (let [type @(rf/subscribe [:table.feed/feed-type])]
    (if (and (= type :product) (seq vs))
      (some #(and (= (:to (second %)) "sku")
                  (:enabled (second %)))
            vs)
      true)))

(defn timezone-string []
  (let [offset (-> (.getTimezoneOffset (js/Date.))
                   (/ 60)
                   (* -1))]
    (str "(UTC" (if (neg? offset) "-" "+") offset ") "
         (.-timeZone (.resolvedOptions (js/Intl.DateTimeFormat))))))

(spec/def ::name
  (spec/and string?
            (complement str/blank?)
            #(<= (count %) 70)))

(spec/def ::url (spec/and string?
                          (complement str/blank?)
                          (spec/or :s3 s3-url? :http url?)))

(spec/def ::api-key (spec/nilable string?))
(spec/def ::api-key-field-type #{"none", "query-parameter", "header-parameter"})
(spec/def ::api-key-field-name (spec/nilable string?))

(spec/def ::enabled boolean?)

(spec/def ::to
  (spec/and string?
            (complement str/blank?)
            #(<= (count %) 70)
            valid-placeholder-name?))

(spec/def ::raw-mapping
  (spec/or :disabled #(not (:enabled %))
           :enabled (spec/keys :req-un [::from ::enabled ::to])))

(spec/def ::raw-mappings
  (spec/and (spec/coll-of ::raw-mapping)
            sku-used?
            #(distinct-elements? :to %)))

(spec/def ::value
  (spec/keys :req-un [::name ::url]
             :opt-un [::api-key
                      ::api-key-field-type
                      ::api-key-field-name
                      ::raw-mappings]))

(p/defphraser #(distinct-elements? k %)
  [_ {:keys [val] :as problem} k]
  (let [idxs (->> val
                  (map-indexed #(assoc (second %2) :idx %1))
                  (filter #(= (:enabled %) true))
                  (group-by k)
                  (some (fn [[_n mapping]]
                          (when (< 1 (count mapping))
                            (mapv :idx mapping)))))]
    {:message "The fields should have unique names"
     :ins (map #(concat (:in problem) [% k]) idxs)
     :problem problem}))

(p/defphraser sku-used?
  [_ problem k]
  {:message "One field needs to be mapped to the SKU field"
   :ins [[:meta :sku]]
   :problem problem})

(p/defphraser (complement str/blank?)
  [_ problem]
  {:message "The field should not be empty"
   :in (:in problem)
   :problem problem})

(p/defphraser string?
  [_ problem key]
  {:message "The field is required"
   :in (:in problem)
   :problem problem})

(p/defphraser int?
  [_ problem key]
  {:message "The field is required"
   :in (:in problem)
   :problem problem})

(def interval-options
  [{:label "No schedule"
    :value nil
    :id :none}
   {:label "Every 10 minutes"
    :value "0 */10 * ? * *"
    :id :10minutes}
   {:label "Every 1 hour"
    :value "0 0 * ? * *"
    :id :hourly}
   {:label "Every 1 day"
    :value "0 0 0 * * ?"
    :id :daily}])

(def reduced-interval-options
  (->> interval-options
       (filterv (comp #{:none :daily} :id))))

(defn ->external-mappings
  [m]
  (let [mappings (keep
                  #(when (:enabled %)
                     (dissoc % :enabled))
                  (:raw-mappings m))]
    (if (empty? mappings)
      m
      (assoc m :mappings mappings))))

(defn ->external-fields-settings
  [settings]
  (mapv (fn [field]
          (merge {:fieldname (key field)}
                 (dissoc (val field) :postcode)))
        settings))

(defn ->internal-fields-settings
  [settings]
  (reduce (fn [acc cur]
            (let [postcode? (boolean (:postcode-type cur))]
              (assoc acc (:fieldname cur)
                     (-> cur
                         (dissoc :fieldname)
                         (assoc :postcode postcode?)))))
          {} settings))

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.feed-importer ::value)))

(def ui-messages
  {:create {:success "Successfully added feed"
            :failure "Failed to add feed"}
   :edit {:success "Successfully saved feed"
          :failure "Failed to save feed"}
   :delete {:success "Successfully deleted feed"
            :failure "Failed to delete feed"}})

(defn lookup-flow
  [feed-id]
  {:id :form.feed-importer/lookup-flow
   :first-dispatch [:resource.feed-importers/get-content-variant feed-id]
   :rules [{:when :seen?
            :events :resource.feed-importers/get-content-variant-success
            :halt? true
            :dispatch [:form.feed-importer/get-lookup-success]}
           {:when :seen?
            :events :resource.feed-importers/get-content-variant-failure
            :halt? true}]})

(rf/reg-event-fx :form.feed-importer/open
  (fn [{:keys [db]} [_ initial-value]]
    {:db (assoc-in db [:form.feed-importer] {:value initial-value :edit-mode (if initial-value :edit :create)})
     :fx [[:dispatch [:ocp/open {:id :feed/crud
                                 :on-close [:form.feed-importer/on-close]}]]
          [:dispatch [:form.feed-importer/init initial-value]]
          (when initial-value [:async-flow (lookup-flow (:id initial-value))])
          (when initial-value [:dispatch [:resource.feed-importers/get-fields (:id initial-value)]])
          (when initial-value [:forward-events [{:register ::get-fields-success
                                                 :events #{:resource.feed-importers/get-fields-success}
                                                 :dispatch-to [:form.feed-importer/get-fields-success]}]])]}))

(rf/reg-event-db :form.feed-importer/get-lookup-success
  (fn [db _]
    (let [{:keys [id fields-settings]} (get-in db [:resource.feed-importers/content-variant :value])
          settings (->internal-fields-settings fields-settings)]
      (assoc-in db [:form.feed-lookup :value] {:id id
                                               :settings settings}))))

(rf/reg-event-fx :form.feed-importer/get-fields-success
  (fn [{:keys [db]} [_ [_ fields]]]
    (let [initial-value (get-in db [:form.feed-importer :value])
          initial-value* (mapv (fn [field]
                                 (let [mapping (->> (:mappings initial-value)
                                                    (filter #(= (:from %) field))
                                                    first)]
                                   (if mapping
                                     (assoc mapping :enabled true)
                                     {:from field :to nil :enabled false})))
                               fields)]
      {:db (assoc-in db [:form.feed-importer :value :raw-mappings] initial-value*)
       :fx [[:forward-events [{:unregister ::get-fields-success}]]]})))

(rf/reg-event-fx :form.feed-importer/trigger
  (fn [_ [_ feed]]
    {:fx [[:dispatch [:resource.feed-importers/trigger (:id feed)]]
          [:forward-events [{:register ::trigger-success
                             :events #{:resource.feed-importers/trigger-success}
                             :dispatch-to [:form.feed-importer/trigger-success]}
                            {:register ::trigger-failure
                             :events #{:resource.feed-importers/trigger-failure}
                             :dispatch-to [:form.feed-importer/trigger-failure]}]]]}))

(rf/reg-event-fx :form.feed-importer/trigger-success
  (fn [_ [_ [event response]]]
    {:fx [[:invalidate-queries {:queryKey ["feed-importers"]}]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message "Importer started"
                       :event event
                       :response response}]]
          [:forward-events [{:unregister ::trigger-success}]]]}))

(rf/reg-event-fx :form.feed-importer/trigger-failure
  (fn [_ [_ [event id response]]]
    {:fx [[:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :error
                       :message (str "Importer " id " is already running")
                       :event event
                       :response response}]]
          [:forward-events [{:unregister ::trigger-failure}]]]}))

(rf/reg-event-fx :form.feed-importer/submit-form
  [validate-form
   (rf/inject-cofx :sub :form.feed-importer/edit-mode)
   (rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [db form.feed-importer/edit-mode resource.user/token url-context]} [_ payload next-tab]]
    (let [type (get-in db [:table.feed/meta :type])
          payload* (-> (if (= :edit edit-mode)
                         (->external-mappings payload)
                         payload)
                       (dissoc :raw-mappings)
                       (dissoc :settings)
                       (assoc :type type))
          callbacks {:on-success [:form.feed-importer/submit-form-success next-tab]
                     :on-failure [:form.feed-importer/submit-form-failure]}]
      (-> {:db db}
          (mr/update-db assoc-in [:form.feed-importer :state] edit-mode)
          (loader.db/open-loader)
          (as-> effects
            (if (= :edit edit-mode)
              (feed-imports/http-patch-feed effects url-context payload* token callbacks)
              (feed-imports/http-post-feed  effects url-context payload* token callbacks)))))))

(rf/reg-event-fx :form.feed-importer/submit-form-success
  (fn [{:keys [db]} [_ next-tab response]]
    (-> {:db db}
        (feed-imports/on-feed-importers-one-success response)
        (loader.db/close-loader)
        (mr/update-db (fn [db]
                        (let [context (get-in db [:form.feed-importer :value :context])]
                          (-> db
                              (assoc-in [:form.feed-importer] {:value response :edit-mode :edit})
                              (assoc-in [:form.feed-importer :value :context] context)
                              (assoc-in [:form.feed-lookup :value :id] (:id response))))))
        (mr/conj-fx [:forward-events [{:register ::get-fields-success
                                       :events #{:resource.feed-importers/get-fields-success}
                                       :dispatch-to [:form.feed-importer/get-fields-success]}]]
                    [:dispatch [:resource.feed-importers/get-fields (:id response)]]
                    [:invalidate-queries {:queryKey ["feed-importers"]}])
        (mr/conj-fx (let [state (get-in db [:form.feed-importer :state])]
                      [:dispatch [:response-handler/handle-response
                                  {:entity-name entity-name
                                   :type :success
                                   :message (get-in ui-messages [state :success])}]]))
        (cond-> (nil? (next-tab)) (-> (mr/conj-fx [:dispatch [:form.feed-importer/trigger response]])
                                      (mr/conj-fx [:dispatch [:ocp/close]]))))))

(rf/reg-event-fx :form.feed-importer/submit-form-failure
  (fn [{:keys [db]} [event response]]
    (-> {:db db}
        (loader.db/close-loader)
        (mr/conj-fx [:forward-events [{:unregister ::delete-failure}
                                      {:unregister ::context-mapping-failure}]])
        (mr/conj-fx (let [state (get-in db [:form.feed-importer :state])]
                      [:dispatch [:response-handler/handle-response
                                  {:entity-name entity-name
                                   :type :error
                                   :event event
                                   :message (get-in ui-messages [state :failure])
                                   :response response}]])))))

(rf/reg-event-fx :form.feed-importer/delete-feed
  (fn [{:keys [db]} _]
    (let [feed-id (get-in db [:form.feed-importer :value :id])]
      {:db (assoc-in db [:form.feed-importer :state] :delete)
       :fx [[:dispatch [:resource.feed-importers/delete feed-id]]
            [:forward-events [{:register ::delete-success
                               :events #{:resource.feed-importers/delete-success}
                               :dispatch-to [:form.feed-importer/delete-feed-success]}
                              {:register ::delete-failure
                               :events #{:resource.feed-importers/delete-failure}
                               :dispatch-to [:form.feed-importer/submit-form-failure]}]]]})))

(rf/reg-event-fx :form.feed-importer/delete-feed-success
  (fn [_ _]
    {:fx [[:forward-events [{:unregister ::delete-success}]]
          [:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:invalidate-queries {:queryKey ["feed-importers"]}]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [:delete :success])}]]]}))

(rf/reg-event-fx :form.feed-importer/save-lookup
  (fn [{:keys [db]} _]
    (let [{:keys [id settings]} (get-in db [:form.feed-lookup :value])
          field-settings (->external-fields-settings settings)]
      {:fx [[:async-flow {:id :form.feed-importer/save-lookup-flow
                          :first-dispatch [:resource.feed-importers/put-content-variant id {:fields-settings field-settings}]
                          :rules [{:when :seen?
                                   :events :resource.feed-importers/put-content-variant-success
                                   :halt? true
                                   :dispatch [:form.feed-importer/save-lookup-settings-success]}
                                  {:when :seen?
                                   :events :resource.feed-importers/put-content-variant-failure
                                   :halt? true
                                   :dispatch [:form.feed-importer/submit-form-failure]}]}]]})))

(rf/reg-event-fx :form.feed-importer/save-lookup-settings-success
  (fn [_ _]
    {:fx [[:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [:edit :success])}]]]}))

(defn- name-exists?
  [name values]
  (boolean (seq (filter #(= (:to %) name) values))))

(defn- resolve-duplicates
  [name values]
  (if (name-exists? name values)
    (loop [idx 1]
      (let [indexed-name (str name "_" idx)]
        (if-not (name-exists? indexed-name values)
          indexed-name
          (recur (inc idx)))))
    name))

(rf/reg-event-fx :form.feed-importer/select-all-rows
  (fn [{:keys [db]}]
    (let [mapping-count (count (get-in db [:form.feed-importer :value :raw-mappings]))]
      {:fx (mapv #(vector :dispatch [:form.feed-importer/select-row %]) (range mapping-count))})))

(rf/reg-event-fx :form.feed-importer/deselect-all-rows
  (fn [{:keys [db]}]
    (let [mapping-count (count (get-in db [:form.feed-importer :value :raw-mappings]))]
      {:fx (mapv #(vector :dispatch [:form.feed-importer/deselect-row %]) (range mapping-count))})))

(rf/reg-event-fx :form.feed-importer/select-row
  [validate-form]
  (fn [{:keys [db]} [_ idx]]
    (let [type (get-in db [:table.feed/meta :type])
          autofill-event :form.feed-importer/autofill-reference
          field-path [:form.feed-importer :value :raw-mappings idx]
          reference (get-in db (conj field-path :to))]
      {:db (assoc-in db (conj field-path :enabled) true)
       :fx [(when (and (empty? reference) (not= type :product)) [:dispatch [autofill-event idx]])]})))

(rf/reg-event-db :form.feed-importer/deselect-row
  [validate-form]
  (fn [db [_ idx]]
    (let [field-path [:form.feed-importer :value :raw-mappings idx]]
      (assoc-in db (conj field-path :enabled) false))))

(rf/reg-event-db :form.feed-importer/autofill-reference
  [validate-form]
  (fn [db [_ idx]]
    (let [mappings (get-in db [:form.feed-importer :value :raw-mappings])
          fieldname (:from (nth mappings idx))
          sanitized-fieldname (util/sanitize-fieldname fieldname)
          reference (resolve-duplicates sanitized-fieldname mappings)]
      (cond-> (assoc-in db [:form.feed-importer :value :raw-mappings idx :to] reference)

        (not= sanitized-fieldname fieldname)
        (update-in [:form.feed-importer :sanitized-fields] conj idx)

        (not= sanitized-fieldname reference)
        (update-in [:form.feed-importer :indexed-fields] conj idx)))))

(rf/reg-sub :form.feed-importer/row-selected?
  (fn [db [_ idx]]
    (get-in db [:form.feed-importer :value :raw-mappings idx :enabled])))

(rf/reg-sub :form.feed-importer/selected-state
  :<- [:form.feed-importer/value :raw-mappings]
  (fn [mappings _]
    (cond
      (not-any? :enabled mappings) :none
      (every? :enabled mappings) :all
      :else :partial)))

(rf/reg-event-fx :form.feed-importer/on-close
  (fn [{:keys [db]} _]
    {:fx [[:dispatch [:form.feed-importer/destroy]]
          [:dispatch [:form.feed-lookup/destroy]]]
     :db (dissoc db :resource.feed-importers/fields)}))

(rf/reg-sub :form.feed-importer/edit-mode
  (fn [db _]
    (get-in db [:form.feed-importer :edit-mode])))

(rf/reg-sub :form.feed-importer/url
  (fn [db _]
    (get-in db [:form.feed-importer :value :url])))

(rf/reg-sub :form.feed-importer/sanitized-fields
  (fn [db _]
    (get-in db [:form.feed-importer :sanitized-fields])))

(rf/reg-sub :form.feed-importer/indexed-fields
  (fn [db _]
    (get-in db [:form.feed-importer :indexed-fields])))

(rf/reg-sub :form.feed-importer/mapped-fields
  :<- [:form.feed-importer/value :mappings]
  (fn [mappings _]
    {:state :success
     :value (map :to mappings)}))

(rf/reg-sub :form.feed-importer/raw-mapping-errors
  :<- [:form.feed-importer/error [:raw-mappings]]
  (fn [raw-mapping-errors _]
    (-> raw-mapping-errors
        vals
        (->> (mapcat :to))
        (->> (map :message))
        distinct
        sort)))

(rf/reg-sub :resource.feed-importers/sanitised-fields
  :<- [:resource.feed-importers/fields-value]
  (fn [field-names _]
    (map util/sanitize-fieldname field-names)))

(def use-error-message (partial form/use-error-message :form.feed-importer))

(def use-bind-control (partial form/use-bind-control :form.feed-importer))

(form/register-form {:namespace :form.feed-importer
                     :initial-value {:name "" :url ""}
                     :validate-form validate-form
                     :submit-event :form.feed-importer/submit-form})
